import dayjs, { OpUnitType } from 'dayjs';
import isNull from 'lodash/isNull';
import mapValues from 'lodash/mapValues';
import round from 'lodash/round';

import { DateRange, PriceType } from '@cast/shared-types';
import { getPriceMultiplier } from '@cast/utils';

import {
  CostReportChartDataByPeriod,
  CostReportDataByPeriod,
  ResourceOfferingMultiplier,
} from '../types/costOverTime';

export const unitsInCurrentRange = (
  [start, end]: DateRange,
  unit: OpUnitType
): number => {
  return dayjs(end).diff(start, unit);
};

export const hasFallbackDataPoint = ({
  fallbackCpuCount,
}: CostReportChartDataByPeriod): boolean => fallbackCpuCount !== 0;

const hoursTillNow = (timezone?: string): number => {
  const now = timezone ? dayjs().tz(timezone) : dayjs();
  return Math.max(1, now.diff(now.startOf('d'), 'h'));
};

const isCurrentDayData = (
  data: CostReportChartDataByPeriod,
  timezone?: string
): boolean => {
  const date = timezone
    ? dayjs.utc(data.timestamp).tz(timezone)
    : dayjs(data.timestamp);
  const today = timezone ? dayjs().tz(timezone) : dayjs();
  return date.isSame(today, 'day');
};

export const getDataHoursMultiplier = (
  data: CostReportChartDataByPeriod,
  priceType: PriceType = PriceType.DAILY,
  timezone?: string
): number => {
  if (priceType === PriceType.DAILY && isCurrentDayData(data, timezone)) {
    return hoursTillNow(timezone);
  }

  return getPriceMultiplier(priceType);
};

export const hasCpuAndNotForecastedDataPoint = ({
  onDemandCpuCount,
  fallbackCpuCount,
  spotCpuCount,
  forecast,
}: CostReportDataByPeriod): boolean =>
  (!!onDemandCpuCount || !!fallbackCpuCount || !!spotCpuCount) && !forecast;

export const hasGpuAndNotForecastedDataPoint = ({
  onDemandGpuCount,
  fallbackGpuCount,
  spotGpuCount,
  forecast,
}: CostReportDataByPeriod): boolean =>
  (!!onDemandGpuCount || !!fallbackGpuCount || !!spotGpuCount) && !forecast;

export const isPastDataPoint = (
  timestamp: string,
  timezone: string
): boolean => {
  return !dayjs.utc(timestamp).tz(timezone).isToday();
};

export const getUptimeMultiplier = ({
  onDemandUptime,
  fallbackUptime,
  spotUptime,
}: CostReportChartDataByPeriod): ResourceOfferingMultiplier => {
  const onDemand = (onDemandUptime ?? 0) / 60;
  const fallback = (fallbackUptime ?? 0) / 60;
  const spot = (spotUptime ?? 0) / 60;

  // storage costs already have uptime accounted for...
  const storage = 1;

  return {
    onDemand,
    fallback,
    spot,
    storage,
  };
};

const roundPodCount = (count?: number): number => {
  return count ? round(count) : 0;
};

export const preparePodCount = (
  onDemand?: number,
  fallback?: number,
  spot?: number
): {
  total: number | null;
  onDemand: number | null;
  fallback: number | null;
  spot: number | null;
} => {
  if (isNull(onDemand) || isNull(fallback) || isNull(spot)) {
    return {
      total: null,
      onDemand: null,
      fallback: null,
      spot: null,
    };
  }

  const onDemandCount = roundPodCount(onDemand);
  const fallbackCount = roundPodCount(fallback);
  const spotCount = roundPodCount(spot);
  const totalCount = onDemandCount + fallbackCount + spotCount;

  return {
    total: totalCount,
    onDemand: onDemandCount,
    fallback: fallbackCount,
    spot: spotCount,
  };
};

export const convertChartDatapointToCostReportData = (
  data: CostReportChartDataByPeriod[]
): CostReportDataByPeriod[] => {
  return data.map((d) => {
    return mapValues(d, (value) => {
      return value !== null ? value : 0;
    }) as CostReportDataByPeriod;
  });
};
